import React, { useCallback, useState } from 'react';
import GmailConsentPrimer, {
  GmailConsentPrimerVariant,
} from '../components/email/GmailConsentPrimer';
import { captureEvent } from '../services/analytics';

/**
 * Gates every "connect Gmail" entry point behind the consent primer (see
 * GmailConsentPrimer): the trigger calls `openPrimer()` instead of redirecting,
 * and Continue runs the exact redirect logic the call site used before —
 * `startConnect` is invoked synchronously inside the Continue click, so
 * popup-blocker-sensitive `window.open` calls still sit in a user gesture.
 *
 * Render `primer` once in the host component's JSX.
 */
export function useGmailConsentPrimer(startConnect: () => void): {
  openPrimer: (variant?: GmailConsentPrimerVariant) => void;
  primer: React.ReactElement;
} {
  const [open, setOpen] = useState(false);
  const [variant, setVariant] = useState<GmailConsentPrimerVariant>('connect');

  const openPrimer = useCallback((v: GmailConsentPrimerVariant = 'connect') => {
    setVariant(v);
    setOpen(true);
    captureEvent('gmail_consent_primer_shown', { variant: v });
  }, []);

  const handleContinue = () => {
    setOpen(false);
    captureEvent('gmail_consent_primer_continue', { variant });
    startConnect();
  };

  // Fires for the Cancel button, the X, and the mask alike — every way out
  // short of Continue is the funnel dropout this feature exists to measure.
  const handleCancel = () => {
    setOpen(false);
    captureEvent('gmail_consent_primer_cancel', { variant });
  };

  const primer = (
    <GmailConsentPrimer
      open={open}
      variant={variant}
      onContinue={handleContinue}
      onCancel={handleCancel}
    />
  );

  return { openPrimer, primer };
}
